import { KeyboardEvent, MouseEvent, ReactNode, useRef, useEffect, useState } from 'react'
import { cn } from '@utils/cn'

interface PdfTileDetail {
  readonly text: string
  readonly className?: string
}

interface PdfTileProps {
  readonly label: string
  readonly subtitle?: string
  /** Badges estruturados (ex.: folhas de obra — mecânico, tipo, estado). */
  readonly details?: PdfTileDetail[]
  /** Conteúdo livre no topo (ex.: badges personalizados). Preferir `details` quando possível. */
  readonly topContent?: ReactNode
  /** Quando definido, o tile é um link direto (evita bloqueio de pop-ups ao abrir PDF). */
  readonly href?: string
  readonly onClick?: () => void
  readonly className?: string
  readonly isLoading?: boolean
  readonly muted?: boolean
}

export function PdfTile({
  label,
  subtitle,
  details,
  topContent,
  href,
  onClick,
  className,
  isLoading = false,
  muted = false,
}: Readonly<PdfTileProps>) {
  const textRef = useRef<HTMLSpanElement>(null)
  const anchorRef = useRef<HTMLAnchorElement>(null)
  const divRef = useRef<HTMLDivElement>(null)
  const [fontSize, setFontSize] = useState<string>('0.75rem')

  const hasBadges = !!(topContent || (details && details.length > 0))

  useEffect(() => {
    const adjustFontSize = () => {
      const container = anchorRef.current ?? divRef.current
      if (!textRef.current || !container) return
      const textElement = textRef.current

      const containerWidth = container.offsetWidth
      if (containerWidth === 0) return

      const isMobile = containerWidth < 220
      const padding = isMobile ? 12 : 40
      const availableWidth = containerWidth - padding
      const maxSize = isMobile ? (hasBadges ? 8 : 9) : 14

      let calculatedSize = Math.max(7, Math.min(maxSize, availableWidth * (isMobile ? 0.055 : 0.08)))

      textElement.style.fontSize = `${calculatedSize}px`
      textElement.style.lineHeight = '1.2'

      const lineHeight = calculatedSize * 1.2
      const maxHeight = lineHeight * 2

      if (textElement.scrollHeight > maxHeight && calculatedSize > 7) {
        while (calculatedSize > 7 && textElement.scrollHeight > maxHeight) {
          calculatedSize -= 0.5
          textElement.style.fontSize = `${calculatedSize}px`
          const newLineHeight = calculatedSize * 1.2
          const newMaxHeight = newLineHeight * 2
          if (textElement.scrollHeight <= newMaxHeight) break
        }
      }

      setFontSize(`${calculatedSize}px`)
    }

    const timeoutId = setTimeout(adjustFontSize, 0)

    let resizeObserver: ResizeObserver | null = null
    const container = anchorRef.current ?? divRef.current
    if (container) {
      resizeObserver = new ResizeObserver(() => {
        adjustFontSize()
      })
      resizeObserver.observe(container)
    }

    return () => {
      clearTimeout(timeoutId)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [label, hasBadges])

  const isInteractive = !!(href || onClick)
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement | HTMLAnchorElement>) => {
    if (!onClick && !href) return
    if (event.key === 'Enter' || event.key === ' ') {
      if (href) return
      event.preventDefault()
      onClick?.()
    }
  }

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (href) return
    if (onClick) {
      e.preventDefault()
      e.stopPropagation()
      onClick()
    }
  }

  const tileClassName = cn(
    'group relative flex w-full flex-col items-center rounded-[12px] md:rounded-[28px] border border-secondary-200 bg-white text-center shadow-sm transition-all duration-200',
    hasBadges
      ? 'min-h-[12.5rem] justify-start gap-1.5 p-2 pt-2 md:min-h-0 md:aspect-[3/4] md:justify-center md:gap-3 md:p-5'
      : 'aspect-[7/5] justify-center gap-1 p-2 md:aspect-[3/4] md:gap-4 md:p-5',
    isInteractive
      ? 'cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-primary-300 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
      : 'cursor-default',
    isLoading && 'pointer-events-none animate-pulse opacity-70',
    muted && 'opacity-60',
    className
  )

  const structuredBadges =
    details && details.length > 0 ? (
      <div className="flex max-w-full flex-wrap items-center justify-center gap-1 md:flex-col md:gap-1.5">
        {details.map((detail, index) => (
          <span
            key={index}
            className={cn(
              'inline-flex max-w-full items-center justify-center rounded-full border px-1.5 py-0.5 text-center text-[9px] font-semibold leading-tight shadow-sm line-clamp-1 md:px-2.5 md:py-1 md:text-[11px]',
              detail.className ?? 'border-secondary-200 bg-secondary-50 text-secondary-600'
            )}
            title={detail.text}
          >
            {detail.text}
          </span>
        ))}
      </div>
    ) : null

  const badgeArea = topContent ? (
    <div className="max-w-full [&_.inline-flex]:max-w-full [&_.inline-flex]:text-[9px] [&_.inline-flex]:leading-tight [&_.inline-flex]:line-clamp-1 md:[&_.inline-flex]:text-[11px] [&>div]:flex [&>div]:max-w-full [&>div]:flex-wrap [&>div]:items-center [&>div]:justify-center [&>div]:gap-1 md:[&>div]:flex-col md:[&>div]:gap-1.5">
      {topContent}
    </div>
  ) : (
    structuredBadges
  )

  const content = (
    <>
      {badgeArea && <div className="w-full shrink-0 px-0.5">{badgeArea}</div>}

      <div
        className={cn(
          'flex w-full min-h-0 flex-1 flex-col items-center justify-center gap-1 md:gap-3',
          hasBadges && 'py-0.5'
        )}
      >
        <div
          className={cn(
            'flex shrink-0 flex-col overflow-hidden rounded-[10px] border border-secondary-200 bg-white shadow-inner md:rounded-[20px]',
            hasBadges ? 'h-10 w-8 md:h-20 md:w-16' : 'h-12 w-10 md:h-20 md:w-16'
          )}
        >
          <div className="bg-red-600 py-0.5 text-center text-[5px] font-black tracking-[0.12em] text-white md:py-1 md:text-[11px] md:tracking-[0.35em]">
            PDF
          </div>
          <div className="flex flex-1 items-center justify-center text-[5px] font-semibold uppercase tracking-[0.12em] text-secondary-400 md:text-[10px] md:tracking-[0.35em]">
            DOC
          </div>
        </div>

        <span
          ref={textRef}
          className="w-full shrink-0 px-1 font-semibold uppercase tracking-[0.15em] text-secondary-700 line-clamp-2 break-words md:px-2 md:tracking-[0.28em]"
          style={{
            fontSize: fontSize,
            lineHeight: '1.2',
          }}
        >
          {label}
        </span>
        {subtitle && (
          <span className="mt-0.5 w-full shrink-0 px-1 text-[9px] text-secondary-500 line-clamp-2 break-words md:mt-1 md:px-2 md:text-[11px]">
            {subtitle}
          </span>
        )}
      </div>
    </>
  )

  if (href && !muted && !isLoading) {
    return (
      <a
        ref={anchorRef}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={tileClassName}
        aria-label={`Abrir documento: ${label}`}
      >
        {content}
      </a>
    )
  }

  return (
    <div
      ref={divRef}
      role={onClick ? 'button' : 'presentation'}
      tabIndex={onClick ? 0 : -1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={tileClassName}
      aria-label={onClick ? `Abrir documento: ${label}` : undefined}
    >
      {content}
    </div>
  )
}
